import { useState, useCallback } from "react";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { ChevronDownIcon } from "@heroicons/react/outline";
import { DownloadIcon } from "@heroicons/react/outline";
import Button from "components/Button";
import { fmtDate, fmtCurrency, cx } from "utils";
import { TransformedInvoice } from "../types";
import Card from "components/Card";
import { trackInvoiceDownloadClicked } from "utils/Tracking";


const STATUS_STYLES: Record<string, { label: string; className: string }> = {
  paid:        { label: "Paid",        className: "bg-green-100 text-green-800" },
  not_paid:    { label: "Not Paid",    className: "bg-red-100 text-red-800" },
  payment_due: { label: "Payment Due", className: "bg-red-100 text-red-800" },
  posted:      { label: "Posted",      className: "bg-amber-100 text-amber-800" },
  voided:      { label: "Voided",      className: "bg-neutral-100 text-neutral-500" },
};

function InvoiceStatusBadge({ status }: { status: string }) {
  const style = STATUS_STYLES[status] || {
    label: status,
    className: "bg-neutral-100 text-neutral-600",
  };

  return (
    <span className={cx("px-2 py-0.5 rounded text-xs font-medium", style.className)}>
      {style.label}
    </span>
  );
}


interface InvoiceRowProps {
  invoice: TransformedInvoice;
  onDownload: (invoiceId: string) => Promise<void>;
  onPayNow?: (invoiceId: string) => void;
  payNowLoading?: boolean;
  payNowDisabled?: boolean;
  showPayNow?: boolean;
  actionsDisabled?: boolean;
}

export default function InvoiceRow({
  invoice,
  onDownload,
  onPayNow,
  payNowLoading,
  payNowDisabled,
  showPayNow,
  actionsDisabled,
}: InvoiceRowProps) {
  const hasLineItems = invoice.lineItems && invoice.lineItems.length > 0;
  const [isDownloading, setIsDownloading] = useState(false);
  // Dim the informational cells on voided invoices so the row reads as inactive,
  // but leave the action column at full opacity so Download still looks clickable.
  const voidedDim = invoice.status === "voided" ? "opacity-50" : "";

  const handleDownload = useCallback(async () => {
    trackInvoiceDownloadClicked(invoice.id, invoice.invoiceNumber);
    setIsDownloading(true);
    try {
      await onDownload(invoice.id);
    } finally {
      setIsDownloading(false);
    }
  }, [onDownload, invoice.id, invoice.invoiceNumber]);

  return (
    <Disclosure as="tbody">
      {({ open }) => (
        <>
          {/* Summary row — white background so it contrasts with the expanded panel */}
          <DisclosureButton
            as="tr"
            className={cx(
              "border-b border-neutral-200 bg-white",
              hasLineItems && "cursor-pointer hover:bg-neutral-50",
            )}
          >
            {/* Expand indicator */}
            <td className={cx("py-2 pl-2 pr-0 w-8", voidedDim)}>
              {hasLineItems && (
                <ChevronDownIcon
                  className={cx(
                    "w-4 h-4 text-neutral-400 transition-transform",
                    open && "rotate-180",
                  )}
                />
              )}
            </td>

            <td className={cx("py-2 px-4 min-h-[48px] text-sm font-normal leading-5 text-neutral-900 whitespace-nowrap", voidedDim)}>
              {fmtDate(new Date(invoice.date * 1000))}
            </td>

            <td className={cx("py-2 px-4 min-h-[48px] text-sm font-normal leading-5 text-neutral-900", voidedDim)}>
              {invoice.planName}
            </td>

            <td className={cx("py-2 px-4 min-h-[48px] text-sm font-normal leading-5 text-neutral-900 text-right", voidedDim)}>
              {fmtCurrency(invoice.amount, invoice.currencyCode, 2)}
            </td>

            <td className={cx("py-2 px-4 min-h-[48px]", voidedDim)}>
              <InvoiceStatusBadge status={invoice.status} />
            </td>

            <td className={cx("py-2 px-4 min-h-[48px] text-sm font-normal leading-5 text-gray-600", voidedDim)}>
              {invoice.invoiceNumber}
            </td>

            <td className="py-2 px-4 min-h-[48px] text-right">
              <div className="flex gap-2 justify-end" onClick={(e) => e.stopPropagation()}>
                {showPayNow && onPayNow && (
                  <Button
                    text={payNowLoading ? "LOADING..." : "PAY NOW"}
                    onClick={() => onPayNow(invoice.id)}
                    className="button button-primary w-fit"
                    size="SM"
                    disabled={payNowDisabled || actionsDisabled}
                    loading={payNowLoading}
                  />
                )}
                <Button
                  text="DOWNLOAD"
                  icon={{
                    Icon: DownloadIcon,
                    size: "w-4 h-4",
                    position: "leading",
                  }}
                  onClick={handleDownload}
                  className="button button-tertiary w-fit"
                  size="SM"
                  disabled={isDownloading || actionsDisabled}
                  loading={isDownloading}
                />
              </div>
            </td>
          </DisclosureButton>

          {/* Expanded line items — darker background for contrast */}
          {hasLineItems && (
            <DisclosurePanel as="tr" className="bg-neutral-100">
              <td colSpan={7} className="px-8 py-3">
                <Card className="bg-white">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-neutral-500 text-xs uppercase">
                      <th className="text-left py-1 font-semibold">Description</th>
                      <th className="text-right py-1 font-semibold">Qty</th>
                      <th className="text-right py-1 font-semibold">Unit Price</th>
                      <th className="text-right py-1 font-semibold">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.lineItems!.map((item) => (
                      <tr key={item.id} className="border-t border-neutral-200">
                        <td className="py-1.5 text-neutral-700">
                          {item.description || "—"}
                        </td>
                        <td className="py-1.5 text-right text-neutral-700">
                          {item.quantity ?? "—"}
                        </td>
                        <td className="py-1.5 text-right text-neutral-700">
                          {item.unitAmount !== undefined
                            ? fmtCurrency(item.unitAmount, invoice.currencyCode, 2)
                            : "—"}
                        </td>
                        <td className="py-1.5 text-right text-neutral-700">
                          {fmtCurrency(item.amount, invoice.currencyCode, 2)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>

                <div className="mt-3 pt-3 border-t border-neutral-200 flex justify-end">
                  <div className="flex justify-between gap-8 min-w-[260px] text-sm font-semibold text-neutral-900">
                    <span>Total</span>
                    <span>{fmtCurrency(invoice.total, invoice.currencyCode, 2)}</span>
                  </div>
                </div>
                </Card>
              </td>
            </DisclosurePanel>
          )}
        </>
      )}
    </Disclosure>
  );
}
